import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, HardHat, Briefcase, Calendar, User, ChevronRight,
  ShoppingCart, FileText, Image as ImageIcon, FileSpreadsheet,
  FileArchive, File as FileIcon, Download, Trash2, Upload,
  MessageSquare, Eye, Save, Edit2, Info, Paperclip,
} from 'lucide-react';
import {
  trouverChantier, commandes, typesProjet, categoriesDocumentClient,
  formatDate, formatDateTime, formatMontant,
} from '../data/mockData';

const docIcon = {
  pdf: FileText,
  image: ImageIcon,
  doc: FileText,
  xls: FileSpreadsheet,
  zip: FileArchive,
};

const docBadge = {
  pdf: 'danger',
  image: 'info',
  doc: 'sky',
  xls: 'ok',
  zip: 'warn',
};

const docLabel = {
  pdf: 'PDF',
  image: 'Image',
  doc: 'DOC',
  xls: 'XLSX',
  zip: 'ZIP',
};

const statutBadge = (statut) => {
  if (statut === 'livre') return 'ok';
  if (statut === 'etude') return 'warn';
  return 'info';
};

const statutText = (statut) => {
  if (statut === 'livre') return 'Livre';
  if (statut === 'etude') return 'En etude';
  return 'En cours';
};

const ChantierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { chantier, client } = useMemo(() => trouverChantier(id), [id]);
  const [activeTab, setActiveTab] = useState('apercu');
  const [editing, setEditing] = useState(false);
  const [infosClient, setInfosClient] = useState(chantier?.infosClient || '');

  if (!chantier || !client) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/clients')}>
          <ArrowLeft size={16} /> Retour aux clients
        </button>
        <div className="empty-state">
          <HardHat size={28} />
          <p>Chantier introuvable.</p>
        </div>
      </div>
    );
  }

  const typeProjet = typesProjet.find((t) => t.id === chantier.typeProjet) || typesProjet[0];
  const commandesChantier = commandes.filter((c) => c.chantier === chantier.nom);
  const montantTotal = commandesChantier.reduce((sum, c) => sum + c.montantHT, 0);
  const documentsClient = chantier.documentsClient || [];

  const handleSave = () => {
    setEditing(false);
  };

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate(`/clients/${client.id}`)}>
        <ArrowLeft size={16} /> Retour au client {client.nom}
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <HardHat size={22} />
            </div>
            <div>
              <h1 className="page-title">{chantier.nom}</h1>
              <p className="page-subtitle">
                <span className="mono">{chantier.id}</span> - {client.nom}
              </p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge badge-${typeProjet.badge}`}>{typeProjet.label}</span>
          <span className={`badge badge-${statutBadge(chantier.statut)}`}>{statutText(chantier.statut)}</span>
          <button className="btn-ghost"><Edit2 size={14} /> Modifier</button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'apercu' ? 'active' : ''}`}
          onClick={() => setActiveTab('apercu')}
        >
          <Info size={14} /> Apercu
        </button>
        <button
          className={`tab ${activeTab === 'infos-client' ? 'active' : ''}`}
          onClick={() => setActiveTab('infos-client')}
        >
          <Eye size={14} /> Informations client
        </button>
        <button
          className={`tab ${activeTab === 'commandes' ? 'active' : ''}`}
          onClick={() => setActiveTab('commandes')}
        >
          <ShoppingCart size={14} /> Commandes ({commandesChantier.length})
        </button>
      </div>

      {activeTab === 'apercu' && (
        <>
          <div className="kpi-grid kpi-grid-4">
            <div className="kpi-card">
              <div className="kpi-icon"><Briefcase size={22} /></div>
              <div className="kpi-body">
                <span className="kpi-label">Type de projet</span>
                <span className="kpi-value" style={{ fontSize: '1rem' }}>{typeProjet.label}</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon"><ShoppingCart size={22} /></div>
              <div className="kpi-body">
                <span className="kpi-label">Commandes</span>
                <span className="kpi-value">{commandesChantier.length}</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon"><Calendar size={22} /></div>
              <div className="kpi-body">
                <span className="kpi-label">Avancement</span>
                <span className="kpi-value">{chantier.avancement}%</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon"><HardHat size={22} /></div>
              <div className="kpi-body">
                <span className="kpi-label">Volume HT</span>
                <span className="kpi-value">{formatMontant(montantTotal)}</span>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1.25rem' }}>
            <header className="card-header">
              <div className="card-title">
                <HardHat size={18} />
                <h2>Fiche chantier</h2>
              </div>
            </header>
            <div className="card-body info-grid">
              <div>
                <span className="info-label">Identifiant</span>
                <span className="info-value mono">{chantier.id}</span>
              </div>
              <div>
                <span className="info-label">Type de projet</span>
                <span className="info-value">{typeProjet.label}</span>
              </div>
              <div>
                <span className="info-label"><User size={12} /> Responsable</span>
                <span className="info-value">{chantier.responsable || '-'}</span>
              </div>
              <div>
                <span className="info-label">Statut</span>
                <span className="info-value">{statutText(chantier.statut)}</span>
              </div>
              <div>
                <span className="info-label"><Calendar size={12} /> Date de debut</span>
                <span className="info-value">{formatDate(chantier.dateDebut)}</span>
              </div>
              <div>
                <span className="info-label"><Calendar size={12} /> Fin previsionnelle</span>
                <span className="info-value">{formatDate(chantier.dateFinPrevue)}</span>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <span className="info-label">Avancement</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                  <div className="progress" style={{ flex: 1, maxWidth: 320 }}>
                    <div className="progress-bar" style={{ width: `${chantier.avancement}%` }} />
                  </div>
                  <span className="mono">{chantier.avancement}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1.25rem' }}>
            <header className="card-header">
              <div className="card-title">
                <Briefcase size={18} />
                <h2>Client</h2>
              </div>
              <button className="btn-link" onClick={() => navigate(`/clients/${client.id}`)}>
                Voir la fiche client <ChevronRight size={14} />
              </button>
            </header>
            <div className="card-body info-grid">
              <div>
                <span className="info-label">Client</span>
                <span className="info-value">{client.nom}</span>
              </div>
              <div>
                <span className="info-label">Code marche</span>
                <span className="info-value mono">{client.codeMarche}</span>
              </div>
              <div>
                <span className="info-label">Contact</span>
                <span className="info-value">{client.contact}</span>
              </div>
              <div>
                <span className="info-label">Email</span>
                <span className="info-value">{client.email}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'infos-client' && (
        <>
          <div className="info-banner">
            <Eye size={16} />
            <div>
              <strong>Visible cote espace client.</strong>
              <span>
                Les informations et documents ci-dessous sont accessibles a votre client
                depuis son espace dedie (consultation en lecture seule).
              </span>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1.25rem' }}>
            <header className="card-header">
              <div className="card-title">
                <MessageSquare size={18} />
                <h2>Note d information client</h2>
              </div>
              <div className="page-actions">
                <span className="meta-line">
                  Maj : {formatDateTime(chantier.infosClientMaj)} par {chantier.infosClientPar}
                </span>
                {editing ? (
                  <>
                    <button className="btn-ghost btn-small" onClick={() => {
                      setInfosClient(chantier.infosClient || '');
                      setEditing(false);
                    }}>
                      Annuler
                    </button>
                    <button className="btn-primary btn-small" onClick={handleSave}>
                      <Save size={14} /> Enregistrer
                    </button>
                  </>
                ) : (
                  <button className="btn-ghost btn-small" onClick={() => setEditing(true)}>
                    <Edit2 size={14} /> Modifier
                  </button>
                )}
              </div>
            </header>
            <div className="card-body">
              {editing ? (
                <>
                  <textarea
                    className="textarea-client"
                    value={infosClient}
                    onChange={(e) => setInfosClient(e.target.value)}
                    rows={10}
                    placeholder="Ecrivez ici toute information utile au suivi du projet : avancement, points d attention, communication contextuelle..."
                  />
                  <p className="meta-line" style={{ marginTop: '0.5rem' }}>
                    Ce texte sera visible cote espace client des l enregistrement.
                  </p>
                </>
              ) : chantier.infosClient ? (
                <div className="infos-client-preview">
                  {chantier.infosClient.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                  ))}
                </div>
              ) : (
                <div className="empty-state compact">
                  <MessageSquare size={22} />
                  <p>Aucune note communiquee au client pour ce projet.</p>
                  <button className="btn-ghost btn-small" onClick={() => setEditing(true)}>
                    <Edit2 size={14} /> Saisir une note
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="card" style={{ marginTop: '1.25rem' }}>
            <header className="card-header">
              <div className="card-title">
                <Paperclip size={18} />
                <h2>Documents mis a disposition du client ({documentsClient.length})</h2>
              </div>
              <button className="btn-ghost btn-small">
                <Upload size={14} /> Ajouter un document
              </button>
            </header>
            <div className="card-body">
              <div className="dropzone">
                <Upload size={22} />
                <div>
                  <strong>Glissez-deposez les documents partages avec le client</strong>
                  <p className="meta-line">
                    Compte-rendu, planning, plans, photos, DOE, PV... PDF, images, XLSX, DOCX, ZIP - 20 Mo max
                  </p>
                </div>
                <button className="btn-ghost btn-small">
                  <Paperclip size={14} /> Parcourir
                </button>
              </div>

              {documentsClient.length === 0 ? (
                <div className="empty-state compact" style={{ marginTop: '1rem' }}>
                  <Paperclip size={22} />
                  <p>Aucun document partage avec le client pour le moment.</p>
                </div>
              ) : (
                <table className="data-table" style={{ marginTop: '1rem' }}>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Type</th>
                      <th>Categorie</th>
                      <th>Depose le</th>
                      <th>Par</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentsClient.map((doc) => {
                      const Icon = docIcon[doc.type] || FileIcon;
                      const badge = docBadge[doc.type] || 'neutral';
                      return (
                        <tr key={doc.id}>
                          <td>
                            <div className="cell-strong">
                              <Icon size={16} className="cell-icon" />
                              <span className="mono" style={{ fontSize: '0.82rem' }}>{doc.nom}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge-${badge}`}>{docLabel[doc.type] || 'Fichier'}</span>
                          </td>
                          <td>
                            <span className="chip chip-sky">{doc.categorie}</span>
                          </td>
                          <td>{formatDateTime(doc.dateAjout)}</td>
                          <td>{doc.ajoutePar}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn-ghost btn-small" title="Telecharger">
                              <Download size={14} />
                            </button>
                            <button className="btn-ghost btn-small btn-danger" title="Retirer du partage client">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <p className="meta-line" style={{ marginTop: '1rem' }}>
                Categories disponibles : {categoriesDocumentClient.join(' - ')}
              </p>
            </div>
          </div>
        </>
      )}

      {activeTab === 'commandes' && (
        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <ShoppingCart size={18} />
              <h2>Commandes fournisseurs du projet ({commandesChantier.length})</h2>
            </div>
          </header>
          <div className="card-body">
            {commandesChantier.length === 0 ? (
              <div className="empty-state compact">
                <ShoppingCart size={22} />
                <p>Aucune commande rattachee a ce chantier.</p>
              </div>
            ) : (
              <table className="data-table clickable">
                <thead>
                  <tr>
                    <th>Commande</th>
                    <th>Date</th>
                    <th>Fournisseur</th>
                    <th>Etape</th>
                    <th style={{ textAlign: 'right' }}>Montant HT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {commandesChantier.map((cmd) => (
                    <tr key={cmd.id} className="hoverable clickable" onClick={() => navigate(`/commandes/${cmd.id}`)}>
                      <td><span className="mono">{cmd.id}</span></td>
                      <td>{formatDate(cmd.date)}</td>
                      <td>{cmd.fournisseurNom}</td>
                      <td>
                        <span className={`badge badge-${cmd.etape === 'installation' ? 'ok' : 'info'}`}>
                          {cmd.etape}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}><strong>{formatMontant(cmd.montantHT)}</strong></td>
                      <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChantierDetail;
